<template>
  <transition name="modal">
    <div v-if="open" class="fixed inset-0 z-[100] flex items-center justify-center p-4 font-NotoSerifKhmer">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black/60 backdrop-blur-md" @click="handleCloseModal('close')"></div>

      <!-- Modal Content -->
      <div class="relative w-full max-w-md bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.3)] p-6 md:p-8 animate-scale-up text-center">
        
        <!-- Warning Icon -->
        <div class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-500/20 text-red-400 mb-6 border border-red-500/20 shadow-[0_0_15px_rgba(239,68,68,0.2)]">
          <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>

        <!-- Text -->
        <h3 class="text-2xl font-bold text-white mb-2 tracking-tight">{{ t('Confirm Delete') }}</h3>
        <p class="text-white/60 mb-8 leading-relaxed">
          {{ t('Are you sure you want to delete') }} <span class="text-white font-medium">"{{ elementName || t('this item') }}"</span>? {{ t('This action cannot be undone.') }}
        </p>

        <!-- Actions -->
        <div class="flex gap-4">
          <button
            @click="handleCloseModal('close')"
            :disabled="isDeleting"
            class="flex-1 py-3 rounded-xl text-white/70 font-medium hover:bg-white/10 hover:text-white transition-all duration-200 border border-transparent hover:border-white/10"
          >
            {{ t('Cancel') }}
          </button>
          
          <button
            @click="handleDelete(docId, elementCategory)"
            class="flex-1 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-400 hover:to-pink-500 shadow-lg shadow-red-500/20 transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center gap-2"
            :class="isDeleting ? 'cursor-not-allowed opacity-80' : ''"
          >
            <span v-if="isDeleting" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
            <span>{{ isDeleting ? t('Deleting...') : t('Delete') }}</span>
          </button>
        </div>

      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref } from "vue";
import { useI18n } from "vue-i18n";
const { t } = useI18n();
import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase/config";
import useCollection from "@/composables/useDocument";

const props = defineProps({
  docId: String,
  elementName: String,
  elementCategory: String
});

const emit = defineEmits(["onClose"]);
const open = ref(true);
const isDeleting = ref(false);

const handleCloseModal = (status) => {
  open.value = false;
  setTimeout(() => {
    emit("onClose", status);
  }, 300);
};

const handleDelete = async (id, category) => {
  if (!id) return;
  
  isDeleting.value = true;
  try {
    if (category === "users") {
      const removeUserFunction = httpsCallable(functions, 'deleteUserV2');
      await removeUserFunction({ uid: id });
      handleCloseModal('delete');
    } else if (category === "plans") {
      const { removeDoc } = useCollection('plans');
      await Promise.allSettled([
        removeDoc(id)
      ]);
      handleCloseModal('delete');
    }else if (category === "events") {
      const { removeDoc } = useCollection('events');
      await Promise.allSettled([
        removeDoc(id)
      ]);
      handleCloseModal('delete');
    }else if (category === "guests") {
      const { removeDoc } = useCollection('guests');
      await Promise.allSettled([
        removeDoc(id)
      ]);
      handleCloseModal('delete');
    }else if (category === "expenses") {
      const { removeDoc } = useCollection('expenses');
      await Promise.allSettled([
        removeDoc(id)
      ]);
      handleCloseModal('delete');
    }
  } catch (e) {
    console.error(e);
    handleCloseModal('failed');
  } finally {
    isDeleting.value = false;
  }
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.animate-scale-up {
  animation: scaleUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes scaleUp {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
</style>
